import { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  APP_UPDATE_POLL_MS,
  fetchRemoteBuildId,
  isNewerBuild,
  reloadApp,
} from '@/lib/app-update';
import { useOnlineStatus } from '@/hooks/use-online-status';

/**
 * Fixed banner when a newer deploy is available. Reload only on explicit Update
 * so mid-flow work (receiving, labels) is not interrupted.
 */
export function AppUpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(null);
  const remoteBuildIdRef = useRef<string | null>(null);
  // Offline, the offline notice takes this slot; an update can wait until the connection is back.
  const online = useOnlineStatus();

  useEffect(() => {
    const checkForUpdate = () =>
      fetchRemoteBuildId().then((remote) => {
        remoteBuildIdRef.current = remote;
        // Stay dismissed for this build during the session; a different newer build shows again.
        setUpdateAvailable(isNewerBuild(remote) && remote !== dismissedBuildId);
      });

    void checkForUpdate();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };
    const onPageShow = () => {
      void checkForUpdate();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    }, APP_UPDATE_POLL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.clearInterval(intervalId);
    };
  }, [dismissedBuildId]);

  if (!updateAvailable || !online) return null;

  // An open modal (a task, a dialog) sets pointer-events: none on the body, so taps would fall through
  // to it: pointer-events-auto keeps the banner pressable, and stopping pointerdown here keeps the
  // modal from treating a tap on the banner as a tap outside and closing.
  return (
    <div
      role="status"
      aria-live="polite"
      onPointerDown={(event) => event.stopPropagation()}
      className="pointer-events-auto fixed inset-x-0 top-0 z-[10001] flex items-center justify-center gap-3 border-b border-primary/20 bg-primary px-3 py-2 text-primary-foreground shadow-sm"
    >
      <p className="min-w-0 flex-1 text-center text-sm font-medium sm:flex-none">
        A new version is available
      </p>
      <div className="flex shrink-0 items-center gap-1.5">
        <Button
          size="compact"
          variant="secondary"
          className="gap-1.5 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
          onClick={() => reloadApp()}
        >
          <RefreshCw className="size-3.5" />
          Update
        </Button>
        <Button
          size="icon-compact"
          variant="ghost"
          className="text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"
          aria-label="Dismiss update banner"
          onClick={() => {
            setDismissedBuildId(remoteBuildIdRef.current);
            setUpdateAvailable(false);
          }}
        >
          <X className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}
